/**
 * Demo "book a call" (B2B lead-capture) store.
 *
 * Persisted in ``localStorage`` (via the shared ``zustandStorage`` shim) so a
 * demo visitor who dismisses the soft banner — or who has already submitted a
 * lead — never sees the prompt again from the same browser. The BE has NO
 * server identity for ephemeral demo users, so client-side persistence IS the
 * source of truth for "don't re-prompt".
 *
 * Kept SEPARATE from ``demoWelcomeStore`` / ``demoTourStore`` on purpose: this
 * is a soft *conversion* prompt (fired only after an "aha moment" trigger),
 * not part of the once-per-session welcome contract — coupling them would
 * make dismissal semantics drift.
 *
 * Only the persistent booleans + ``triggersFired`` list survive a reload; the
 * ``lastPromptAt`` timestamp is intentionally session-only-in-memory (the
 * rate-limit is a same-session guard, not a persistent one).
 */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { zustandStorage } from "@/lib/storage";

/** Trigger names — kept as a string literal union so callers can't fat-finger. */
export type DemoBookCallTrigger =
  | "uptime_5min"
  | "queries_3"
  | "document_created"
  | "connector_configured";

interface DemoBookCallState {
  /** True once the user has clicked the ``X`` on the banner. Never re-prompt. */
  dismissed: boolean;
  /** True once ``/book-call-lead`` returned 200. Never re-prompt. */
  captured: boolean;
  /** Timestamp (ms) of the most recent prompt-attempt — for in-session rate-limiting. */
  lastPromptAt: number | null;
  /** Set of trigger names that have already fired at least once. */
  triggersFired: ReadonlyArray<DemoBookCallTrigger>;
  /** Whether the modal is currently open (session state, never persisted). */
  modalOpen: boolean;
  setDismissed: (v: boolean) => void;
  setCaptured: (v: boolean) => void;
  markTrigger: (name: DemoBookCallTrigger) => void;
  markPrompted: () => void;
  openModal: () => void;
  closeModal: () => void;
  reset: () => void;
}

export const useDemoBookCallStore = create<DemoBookCallState>()(
  persist(
    (set) => ({
      dismissed: false,
      captured: false,
      lastPromptAt: null,
      triggersFired: [],
      modalOpen: false,
      setDismissed: (v) => set({ dismissed: v }),
      setCaptured: (v) => set({ captured: v }),
      markTrigger: (name) =>
        set((s) =>
          s.triggersFired.includes(name)
            ? s
            : { triggersFired: [...s.triggersFired, name] },
        ),
      markPrompted: () => set({ lastPromptAt: Date.now() }),
      openModal: () => set({ modalOpen: true, lastPromptAt: Date.now() }),
      closeModal: () => set({ modalOpen: false }),
      reset: () =>
        set({
          dismissed: false,
          captured: false,
          lastPromptAt: null,
          triggersFired: [],
          modalOpen: false,
        }),
    }),
    {
      name: "ando.demo-book-call",
      storage: createJSONStorage(() => zustandStorage),
      // Never persist ``lastPromptAt`` — the rate-limit is same-session only.
      partialize: (s) => ({
        dismissed: s.dismissed,
        captured: s.captured,
        triggersFired: s.triggersFired,
      }),
    },
  ),
);
